import React, { Suspense, lazy } from 'react'
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'
import RequireAdmin from './components/admin/RequireAdmin'
import Navigation from './components/common/Navigation'
import Footer from './components/common/Footer'
import PopupDisplay from './components/ui/PopupDisplay'
import ScrollToTop from './components/common/ScrollToTop'
import ScrollToTopButton from './components/common/ScrollToTopButton'
import FloatingWhatsApp from './components/ui/FloatingWhatsApp'
import { ColorProvider } from './contexts/ColorContext'
import { AuthProvider } from './contexts/AuthContext'
import './index.css'

// Lazy load all pages for better performance
// Use the optimized Home page for better performance
const Home = lazy(() => import('./pages/HomeOptimized'))
const About = React.lazy(() => import('./pages/About'))
const Courses = React.lazy(() => import('./pages/Courses'))
const CourseDetailNew = React.lazy(() => import('./pages/CourseDetailNew'))
const Services = React.lazy(() => import('./pages/Services'))
const ServiceDetail = React.lazy(() => import('./pages/ServiceDetail'))
const Contact = React.lazy(() => import('./pages/Contact'))
const Admission = React.lazy(() => import('./pages/Admission'))
const AdmissionForm = React.lazy(() => import('./pages/AdmissionForm'))
const Blog = React.lazy(() => import('./pages/Blog'))
const BlogPost = React.lazy(() => import('./pages/BlogPost'))
const BlogEditor = React.lazy(() => import('./pages/BlogEditor'))
const Admin = React.lazy(() => import('./pages/Admin').then(module => ({ default: module.Admin })))
const CourseEditor = React.lazy(() => import('./pages/CourseEditor'))

// Optimized loading component with skeleton UI
const LoadingSpinner = () => (
  <div className="min-h-screen bg-gray-50">
    {/* Skeleton header */}
    <div className="h-20 bg-white shadow-sm border-b border-gray-100">
      <div className="container mx-auto px-4 h-full flex items-center justify-between">
        <div className="h-10 w-32 bg-gray-200 rounded animate-pulse"></div>
        <div className="flex gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-8 w-20 bg-gray-200 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    </div>
    
    {/* Skeleton content */}
    <div className="container mx-auto px-4 py-12">
      <div className="space-y-8">
        <div className="h-64 bg-gray-200 rounded-lg animate-pulse"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-48 bg-gray-200 rounded-lg animate-pulse"></div>
          ))}
        </div>
      </div>
    </div>
  </div>
)

function AppContent() {
  const location = useLocation()
  const isAdminRoute = location.pathname.startsWith('/admin')
  const isAdmissionFormRoute = location.pathname === '/admission/apply'
  const isImmersiveRoute = isAdminRoute || isAdmissionFormRoute

  return (
    <div className="min-h-screen bg-white">
      <ScrollToTop />
      {!isImmersiveRoute && <Navigation />}
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/courses" element={<Courses />} />
          <Route path="/courses/:courseId" element={<CourseDetailNew />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:slug" element={<ServiceDetail />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/admission" element={<Admission />} />
          <Route path="/admission/apply" element={<AdmissionForm />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/:section" element={<RequireAdmin><Admin /></RequireAdmin>} />
          <Route path="/admin/blog/new" element={<RequireAdmin><BlogEditor /></RequireAdmin>} />
          <Route path="/admin/blog/edit/:id" element={<RequireAdmin><BlogEditor /></RequireAdmin>} />
          <Route path="/admin/course/edit/:courseId" element={<RequireAdmin><CourseEditor /></RequireAdmin>} />
        </Routes>
      </Suspense>
      
      {!isImmersiveRoute && (
        <>
          {/* Footer Image - Lazy loaded */}
          <div className="relative z-10 -mb-1 w-full overflow-hidden">
            <img
              src="/footer.png"
              alt="Nepal Mountains"
              className="h-32 w-full object-cover object-top sm:h-44 lg:h-52"
              loading="lazy"
              decoding="async"
            />
          </div>
          
          <Footer />
        </>
      )}
      
      {/* Popup Display - Show on all non-immersive pages */}
      {!isImmersiveRoute && <PopupDisplay currentPath={location.pathname} />}
      
      {/* Floating WhatsApp Button - Show on all non-immersive pages */}
      {!isImmersiveRoute && <FloatingWhatsApp />}
      
      {/* Scroll to Top Button - Show on all non-immersive pages */}
      {!isImmersiveRoute && <ScrollToTopButton />}
    </div>
  )
}

function App() {
  return (
    <ColorProvider>
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </ColorProvider>
  )
}

export default App
